import { FC, useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useAtomValue, useSetAtom } from "jotai";
import { AnimationValues } from "../../../common/ui";
import { checkMessages, consumeMessage, SnackbarMessage } from "../../../state/view";
import { TransitionUp } from "../../common/Transitions";

/**
 * ViewSnackbar renders the snackbar UI component that is used by View.
 *
 * This component subscribes to shared snackbar state and resets its content when
 * the user closes the temporary message banner.
 *
 * MUI Snackbar ClickAwayListenerProps props are set to ignore click away so that
 * the application can appropriately show temporary messages multiple times.
 *
 * It is important to note that the snackbar should be used to display messages that
 * have no in-view context, like general errors or something similar.
 *
 * Containing component View will always render up to one snackbar at time.
 *
 * @returns         the shared ViewSnackbar component
 */
const ViewSnackbar: FC = () => {
	const consumeNewMessage = useSetAtom(consumeMessage);
	const newMessage = useAtomValue(checkMessages);
	const [cache, setCache] = useState<SnackbarMessage | undefined>(undefined);

	useEffect(() => {
		if (!newMessage) {
			return;
		}

		setCache(newMessage);
		setTimeout(() => {
			consumeNewMessage();
		}, 5000);
	}, [consumeNewMessage, newMessage]);

	const closeSnackbar = () => consumeNewMessage();
	const snackbarDidExit = () => setCache(undefined);

	return (
		<Snackbar
			open={newMessage !== undefined}
			sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
			anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			autoHideDuration={AnimationValues.SnackbarAutoHideDuration}
		>
			<TransitionUp in={newMessage !== undefined} onExited={snackbarDidExit}>
				<Alert onClose={closeSnackbar} severity={cache?.severity} variant="filled">
					{cache?.text}
				</Alert>
			</TransitionUp>
		</Snackbar>
	);
};

export default ViewSnackbar;
